import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Smartphone, Phone, CreditCard } from "lucide-react";

interface EmployeeMobilePhoneTabProps {
  employeeId: string;
  employeeName: string;
  readOnly?: boolean;
}

interface MobilePhone {
  id: string;
  phone_type: string;
  serial_number: string | null;
  email: string | null;
  phone_number: string | null;
  sim_provider: string | null;
  sim_number: string | null;
  tariff: string | null;
  purchase_cost: number | null;
  monthly_cost: number | null;
  assigned_at: string | null;
}

export function EmployeeMobilePhoneTab({ employeeId, employeeName, readOnly = false }: EmployeeMobilePhoneTabProps) {
  const { user } = useAuth();
  const [phone, setPhone] = useState<MobilePhone | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && employeeId) {
      loadAssignedPhone();
    }
  }, [user, employeeId]);

  const loadAssignedPhone = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from("mobile_phones_employee_view")
        .select("id, phone_type, serial_number, email, phone_number, sim_provider, sim_number, tariff, purchase_cost, monthly_cost, assigned_at")
        .eq("assigned_employee_id", employeeId)
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;
      setPhone(data);
    } catch (error) {
      console.error("Error loading assigned phone:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!phone) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Smartphone className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Firmenhandy</CardTitle>
              <CardDescription>Ihr zugewiesenes Mobiltelefon</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Ihnen ist aktuell kein Firmenhandy zugewiesen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Smartphone className="h-6 w-6 text-primary" />
          </div>
          <div>
            <CardTitle>Firmenhandy</CardTitle>
            <CardDescription>Ihr zugewiesenes Mobiltelefon</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Phone Details */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Device Info */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-lg flex items-center gap-2 mb-3">
                  <Smartphone className="h-5 w-5" />
                  Geräteinformationen
                </h3>
                <div className="space-y-3 bg-muted/50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Gerätetyp</span>
                    <Badge variant="secondary">{phone.phone_type}</Badge>
                  </div>
                  {phone.serial_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Seriennummer</span>
                      <span className="font-mono text-sm">{phone.serial_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="font-medium text-lg flex items-center gap-2 mb-3">
                  <Phone className="h-5 w-5" />
                  Kontaktdaten
                </h3>
                <div className="space-y-3 bg-muted/50 p-4 rounded-lg">
                  {phone.phone_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Telefonnummer</span>
                      <a 
                        href={`tel:${phone.phone_number}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {phone.phone_number}
                      </a>
                    </div>
                  )}
                  {phone.email && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">E-Mail</span>
                      <span className="font-medium">{phone.email}</span>
                    </div>
                  )}
                  {!phone.phone_number && !phone.email && (
                    <p className="text-muted-foreground text-sm">Keine Kontaktdaten hinterlegt</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - SIM & Tariff */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-lg flex items-center gap-2 mb-3">
                  <CreditCard className="h-5 w-5" />
                  SIM-Karte & Tarif
                </h3>
                <div className="space-y-3 bg-muted/50 p-4 rounded-lg">
                  {phone.sim_provider && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Provider</span>
                      <span className="font-medium">{phone.sim_provider}</span>
                    </div>
                  )}
                  {phone.sim_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">SIM-Nummer</span>
                      <span className="font-mono text-sm">{phone.sim_number}</span>
                    </div>
                  )}
                  {phone.tariff && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Tarif</span>
                      <Badge variant="outline">{phone.tariff}</Badge>
                    </div>
                  )}
                  {!phone.sim_provider && !phone.sim_number && !phone.tariff && (
                    <p className="text-muted-foreground text-sm">Keine Tarifinformationen hinterlegt</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default EmployeeMobilePhoneTab;
